import { useAppServices } from 'hook/services'
import React, { useEffect, useState } from 'react'

const Onboarding = () => {
  const Service = useAppServices()

  const [steps, setSteps] = useState([])
  const [fetching, setFetching] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' }) 

  const onboardLinks = [
    { label: 'Dashboard page',value:'dashboard' },
    { label: 'Businesses Page', value: 'accounts' },
    { label: 'Leads Page', value: 'leads' },
    { label: 'Submission Page', value: 'submissions' },
    { label: 'Consumers Page', value: 'consumers' },
    { label: 'Customize Theme Page', value: 'settings/branding' },
    { label: 'Domain Configuration Page', value: 'settings/branding?domain' },
    { label: 'Google Api Key Page', value: 'settings/integration?integeration' },
    { label: 'Menu Builder Settings Page', value: 'settings/branding?menu' },
    { label: 'Custom Code Script Page', value: 'settings/integration?custom_code' },
    { label: 'Manage Plans Page', value: 'saas' },
  ]


  const getSetting = async () => {
    try {
      setFetching(true)
      const { response } = await Service.super_admin_settings.get()
      if (response?.data?.onboarding?.length) {
        const formattedSteps = response.data.onboarding.map((item) => ({
          id: Date.now() + Math.random(),
          label: item?.label || '',
          key: item?.key || '',
          value: item?.value || '',
          link: item?.link || '',
          kb_link: item?.kb_link || '',
        }))
        setSteps(formattedSteps)
      } else {
        setSteps([{ id: Date.now(), label: '', key: '', value: '', link: '', kb_link: '' }])
      }
    } catch (err) {
      console.error(err)
      setMessage({ type: 'error', text: 'Failed to fetch onboarding settings' })
    } finally {
      setFetching(false)
    }
  }

  useEffect(() => {
    getSetting()
  }, [])

  // ================= HELPERS =================

  const slugify = (text) =>
  text
    .toLowerCase()            // lowercase
    .trim()                   // remove leading/trailing spaces
    .replace(/\s+/g, '_')     // replace spaces with hyphens
    .replace(/[^\w-]+/g, '')  // remove all non-word characters except hyphens


 const updateStep = (id, field, value) => {
  setSteps((prev) =>
    prev.map((step) => {
      if (step.id !== id) return step

      if (field === 'label') {
        return { ...step, [field]: value, key: slugify(value) }
      }

      return { ...step, [field]: value }
    })
  )
}


  const addStep = () => {
    setSteps((prev) => [...prev, { id: Date.now(), label: '', key: '', value: '', link: '', kb_link: '' }])
  }

  const removeStep = (id) => {
    setSteps((prev) => (prev.length <= 1 ? prev : prev.filter((step) => step.id !== id)))
  }

  // ================= REORDER =================

  const moveStep = (index, direction) => {
    setSteps((prev) => {
      const newSteps = [...prev]
      const targetIndex = direction === 'up' ? index - 1 : index + 1
      if (targetIndex < 0 || targetIndex >= newSteps.length) return newSteps
      const temp = newSteps[index]
      newSteps[index] = newSteps[targetIndex]
      newSteps[targetIndex] = temp
      return newSteps
    })
  }

  // ================= SAVE =================

  const handleSave = async () => {
    try {
      setFetching(true)
      setMessage({ type: '', text: '' })
      const data = steps.map(({ id, ...rest }) => rest)
      const { response } = await Service.super_admin_settings.update({
        payload: { onboarding: data },
      })
      if (response?.success) {
        setMessage({ type: 'success', text: 'Onboarding steps saved successfully' })
        setTimeout(() => setMessage({ type: '', text: '' }), 3000)
      } else {
        setMessage({ type: 'error', text: 'Failed to save onboarding steps' })
      }
    } catch (err) {
      console.error(err)
      setMessage({ type: 'error', text: 'Something went wrong while saving' })
    } finally {
      setFetching(false)
    }
  }

  // ================= UI =================

  return (
    <>
      <style>{`
        .onboarding-wrapper { padding:24px; font-family:Inter,system-ui; }
        .step-row { display:grid; gap:12px; margin-bottom:14px; padding:14px; border:1px solid #e5e7eb; border-radius:12px; background:#fff; box-shadow:0 1px 3px rgba(0,0,0,0.05); }
        input,select { padding:10px 12px; border:1px solid #d1d5db; border-radius:8px; font-size:14px; }
        .action-buttons { display:flex; gap:8px; align-items:center; }
        .icon-btn { width:36px; height:36px; border-radius:50%; border:none; display:flex; align-items:center; justify-content:center; font-size:18px; cursor:pointer; }
        .icon-btn-reorder { width:30px; height:30px; border-radius:50%; border:none; display:flex; align-items:center; justify-content:center; font-size:15px; cursor:pointer; }
        .add-btn{ background:#2563eb; color:white; }
        .remove-btn{ background:#fee2e2; color:#b91c1c; }
        .move-btn { background:#fef3c7; color:#b45309; }
        .save-btn{ margin-top:20px; background:#2563eb; color:white; border:none; padding:12px 22px; border-radius:10px; font-weight:600; cursor:pointer; }
        .message { margin-bottom:16px; padding:12px; border-radius:8px; font-weight:500; }
        .message.success { background:#dcfce7; color:#166534; }
        .message.error { background:#fee2e2; color:#b91c1c; }
      `}</style>

      <div className="onboarding-wrapper">
        <h2>Onboarding Checklist</h2>

        {message.text && <div className={`message ${message.type}`}>{message.text}</div>}

        {fetching ? (
          <div>Loading...</div>
        ) : (
          steps.map((step, index) => (
            <div
              key={step.id}
              className="step-row"
              style={{
                gridTemplateColumns: `${
                  index === 0 || index === steps.length - 1 ? '30px' : '60px'
                } 1fr 1fr 1fr 1fr auto`,
              }}
            >
              <div className="action-buttons">
                {index !== 0 && (
                  <button
                    className="icon-btn-reorder move-btn"
                    onClick={() => moveStep(index, 'up')}
                  >
                    ↑
                  </button>
                )}
                {index !== steps.length - 1 && (
                  <button
                    className="icon-btn-reorder move-btn"
                    onClick={() => moveStep(index, 'down')}
                  >
                    ↓
                  </button>
                )}
              </div>
                <input
                  placeholder="Enter custom label"
                  value={step.label}
                  onChange={(e) => updateStep(step.id, 'label', e.target.value)}
                />

              <input
                placeholder="Enter Description"
                value={step.value}
                onChange={(e) => updateStep(step.id, 'value', e.target.value)}
              />
              <input
                placeholder="Enter Knowledgebase Link"
                value={step.kb_link}
                onChange={(e) => updateStep(step.id, 'kb_link', e.target.value)}
              />

              
                <select
                  value={step.link || ''}
                  onChange={(e) => updateStep(step.id, 'link', e.target.value)}
                >
                  <option value="">Select Link</option>
                  {onboardLinks
                    .map((link) => (
                      <option key={link.value} value={link.value}>
                        {link.label}
                      </option>
                    ))}
                </select>

              <div className="action-buttons">
                {index !== 0 && (
                  <button className="icon-btn remove-btn" onClick={() => removeStep(step.id)}>
                    −
                  </button>
                )}
                <button className="icon-btn add-btn" onClick={addStep}>
                  +
                </button>
              </div>
            </div>
          ))
        )}

        <div style={{ textAlign: 'right' }}>
          <button className="save-btn" onClick={handleSave} disabled={fetching}>
            {fetching ? 'Saving...' : 'Save Onboarding'}
          </button>
        </div>
      </div>
    </>
  )
}

export default Onboarding
